import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Users, Shield, Wrench, Loader2, AlertCircle } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getRecaptchaToken } from '../utils/recaptcha';
import axios from 'axios';
import { landingPath } from '../components/nav/navConfig';

const API = `${window.__BACKEND_URL__}/api`;

const ROLES = [
  { id: 'staff', label: 'Staff Login', icon: Wrench, desc: 'Field workers & technicians' },
  { id: 'supervisor', label: 'Supervisor Login', icon: Users, desc: 'Team leads & foremen' },
  { id: 'admin', label: 'Admin Login', icon: Shield, desc: 'Company administrators' },
];

export default function CompanyLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCompany();
  }, [slug]);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API}/company/by-slug/${slug}`);
      setCompany(res.data);
    } catch {
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const recaptcha_token = await getRecaptchaToken(executeRecaptcha, 'role_login');
      const res = await axios.post(`${API}/auth/role-login`, {
        email, password, role: selectedRole, company_slug: slug, recaptcha_token
      });
      if (res.data.requires_2fa) {
        // 2FA-enabled account: the session token comes from /2fa/verify-login
        navigate('/2fa-verify', {
          state: { email, preAuthToken: res.data.pre_auth_token }
        });
        return;
      }
      if (res.data.first_login) {
        // Populate AuthContext (not just localStorage) so the next route —
        // /change-password-first-login — sees both `token` and `user` and
        // doesn't bounce back to /signin. Previously this only wrote the token
        // to localStorage, but AuthProvider had already mounted with null
        // state, so ChangePasswordFirstLogin's effect (`!token || !user → /signin`)
        // immediately kicked legitimate first-login users out.
        login(res.data.token, res.data.user, null);
        navigate('/change-password-first-login');
      } else {
        login(res.data.token, res.data.user, null);
        // Landing-page choice (Sep 2026): Settings → Profile → "After I sign in".
        try {
          const prefs = await axios.get(`${API}/me/nav-prefs`, { headers: { Authorization: `Bearer ${res.data.token}` } });
          navigate(landingPath(prefs.data?.landing));
        } catch {
          navigate('/clock-in');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1E88E5' }} />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#0B1321' }}>
        <AlertCircle className="w-12 h-12 mb-4" style={{ color: '#EF4444' }} />
        <h1 className="text-xl font-bold text-white mb-2">Company Not Found</h1>
        <p style={{ color: '#A0AEC0' }}>The company URL is invalid or doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#0B1321' }}>
      <div className="w-full max-w-md">
        {/* Logo / Company Name */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: '#1E88E5' }}>
            <span className="text-white font-extrabold text-2xl">{company.company_name.charAt(0)}</span>
          </div>
          <h1 className="text-2xl font-bold text-white">{company.company_name}</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>Workforce Management Portal</p>
        </div>

        {!selectedRole ? (
          /* Role Selection */
          <div className="space-y-3" data-testid="role-selection">
            {ROLES.map((role) => {
              const Icon = role.icon;
              return (
                <button
                  key={role.id}
                  onClick={() => setSelectedRole(role.id)}
                  className="w-full ht-card p-5 flex items-center gap-4 hover:border-blue-500/50 transition-all"
                  data-testid={`role-btn-${role.id}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(30,136,229,0.12)' }}>
                    <Icon className="w-6 h-6" style={{ color: '#1E88E5' }} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">{role.label}</p>
                    <p className="text-xs" style={{ color: '#64748B' }}>{role.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* Login Form */
          <div data-testid="login-form">
            <button onClick={() => { setSelectedRole(null); setError(''); }} className="text-sm mb-4 flex items-center gap-1" style={{ color: '#1E88E5' }}>
              &larr; Back to role selection
            </button>
            <div className="ht-card p-6">
              <h2 className="text-lg font-semibold text-white mb-1 capitalize">{selectedRole} Login</h2>
              <p className="text-xs mb-5" style={{ color: '#64748B' }}>{company.company_name}</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#A0AEC0' }}>Email</label>
                  <input
                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-500"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    placeholder="you@company.com"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#A0AEC0' }}>Password</label>
                  <input
                    type="password" required value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-500"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    placeholder="Enter your password"
                    data-testid="login-password"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="login-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit" disabled={submitting}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 ht-btn-glow"
                  style={{ background: '#1E88E5' }}
                  data-testid="login-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Sign In'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
